import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SyncService, SyncStatus } from '../services/syncService';

interface UseAutoSyncProps {
  storeId: string | null;
  enabled?: boolean;
}

interface UseAutoSyncReturn {
  isSyncing: boolean;
  syncStatus: SyncStatus | null;
  lastSyncMessage: string | null;
  triggerManualSync: () => Promise<void>;
  checkStatus: () => Promise<void>;
}

export const useAutoSync = ({ storeId, enabled = true }: UseAutoSyncProps): UseAutoSyncReturn => {
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [lastSyncMessage, setLastSyncMessage] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  // Verificar estado de sincronización
  const checkStatus = useCallback(async () => {
    if (!storeId || !enabled) return;

    try {
      const status = await SyncService.checkSyncStatus(storeId);
      setSyncStatus(status);
      
      if (status.lastSyncTime) {
        setLastSyncMessage(`Última actualización: hace ${status.hoursAgo.toFixed(1)} horas`);
      } else {
        setLastSyncMessage('Nunca sincronizado');
      }
    } catch (error) {
      console.error('Error checking sync status:', error);
    }
  }, [storeId, enabled]);

  // Ejecutar sync automático
  const executeAutoSync = useCallback(async () => {
    if (!storeId || !enabled || isSyncing) return;

    try {
      setIsSyncing(true);
      setLastSyncMessage('Actualizando datos...');

      const { wasNeeded, result } = await SyncService.smartSync(storeId);
      
      if (wasNeeded) {
        if (result?.success) {
          setLastSyncMessage('✅ Datos actualizados correctamente');
          await checkStatus();
          await queryClient.invalidateQueries();
        } else {
          setLastSyncMessage(`❌ Error: ${result?.message || 'Error desconocido'}`);
        }
      } else {
        setLastSyncMessage('✅ Datos ya están actualizados');
      }

    } catch (error: any) {
      console.error('Error in auto sync:', error);
      setLastSyncMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  }, [storeId, enabled, isSyncing, checkStatus, queryClient]);

  // Trigger manual sync
  const triggerManualSync = useCallback(async () => {
    if (!storeId || isSyncing) return;

    try {
      setIsSyncing(true);
      setLastSyncMessage('Forzando actualización...');

      const result = await SyncService.triggerAutoSync(storeId);
      
      if (result.success) {
        setLastSyncMessage('✅ Actualización forzada completada');
        await checkStatus();
        await queryClient.invalidateQueries();
      } else {
        setLastSyncMessage(`❌ Error: ${result.message}`);
      }

    } catch (error: any) {
      console.error('Error in manual sync:', error);
      setLastSyncMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  }, [storeId, isSyncing, checkStatus, queryClient]);

  // Efecto para verificar estado inicial y ejecutar sync si es necesario
  useEffect(() => {
    if (!storeId || !enabled) {
      hasInitialized.current = false;
      return;
    }
    
    // Evitar re-inicializaciones innecesarias
    if (hasInitialized.current) {
      return;
    }

    const initializeSync = async () => {
      // Obtener status una sola vez - esto actualiza syncStatus en el estado
      const status = await SyncService.checkSyncStatus(storeId);
      setSyncStatus(status);
      
      // Actualizar mensaje basado en el status obtenido
      if (status.lastSyncTime) {
        setLastSyncMessage(`Última actualización: hace ${status.hoursAgo.toFixed(1)} horas`);
      } else {
        setLastSyncMessage('Nunca sincronizado');
      }
      
      // Usar el status obtenido directamente (sin llamar checkStatus nuevamente)
      if (status.needsSync) {
        console.log('🔄 Datos obsoletos detectados, ejecutando sync automático...');
        await executeAutoSync();
      }
      
      hasInitialized.current = true;
    };

    initializeSync();
  }, [storeId, enabled, executeAutoSync]); // Solo dependencias esenciales

  return {
    isSyncing,
    syncStatus,
    lastSyncMessage,
    triggerManualSync,
    checkStatus
  };
};
